import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { HiOutlineArrowCircleDown, HiOutlineLightningBolt } from 'react-icons/hi';
import { useSelector } from 'react-redux';
import { TRADE_FLASH_SOCKET_URL } from '../../config';
import ConnectionStatusBanner from '../../components/ConnectionStatusBanner';
import { formatLocalTime } from '../../utils/marketTime';
import { tradeFlashSocketUrl } from '../../utils/tradeFlashSocket';
import {
  MarketDataDelaySubscribeInline,
} from './components/MarketDataDelaySubscribeHeader';
import RecencyBadge from './components/RecencyBadge';
import SelectTicker from './components/SelectTicker';
import SubscribeUnblurOverlay from './components/SubscribeUnblurOverlay';
import { PREVIEW_TRADE_FLASH_ROWS } from './constants';
import useMarketDataSubscription from './hooks/useMarketDataSubscription';

/** Notional / total delta value may arrive as formatted currency (e.g. "$3,905,000"). Strip symbols and grouping commas before parsing. */
const tradeNotional = (t) => {
  const raw = t?.notional ?? t?.total_delta;
  if (raw == null) return 0;
  if (typeof raw === 'number' && Number.isFinite(raw)) return raw;
  const n = Number(String(raw).replace(/[$,\s]/g, ''));
  return Number.isFinite(n) ? n : 0;
};

const tradeMatchesTicker = (trade, ticker) => {
  if (!ticker) return true;
  return String(trade?.underlying ?? '').toUpperCase() === String(ticker).toUpperCase();
};

const topTenByNotional = (trades) => {
  const cmp = (a, b) =>
    tradeNotional(b) - tradeNotional(a) || (Number(b.timestamp) || 0) - (Number(a.timestamp) || 0);
  return [...trades].sort(cmp).slice(0, 10);
};

const TradeFlash = ({ selected, setSelected }) => {
  const subscribed = useMarketDataSubscription();
  const { theme } = useSelector((state) => state.theme);
  const feedContainerRef = useRef(null);
  const [flashes, setFlashes] = useState([]);
  const [recency, setRecency] = useState(null);
  const [delayMinutes, setDelayMinutes] = useState(15);
  const [isConnected, setIsConnected] = useState(false);
  const isAtBottomRef = useRef(true);
  const delayed = recency === 'delayed';

  const filteredFlashes = useMemo(
    () => (selected ? flashes.filter((t) => tradeMatchesTicker(t, selected)) : flashes),
    [flashes, selected]
  );

  const topTenFlashes = useMemo(() => topTenByNotional(filteredFlashes), [filteredFlashes]);

  const previewFlashes = useMemo(
    () => (subscribed ? filteredFlashes : filteredFlashes.slice(0, PREVIEW_TRADE_FLASH_ROWS)),
    [filteredFlashes, subscribed]
  );
  const lockedFlashes = useMemo(
    () => (subscribed ? [] : filteredFlashes.slice(PREVIEW_TRADE_FLASH_ROWS)),
    [filteredFlashes, subscribed]
  );
  const previewTopTen = useMemo(
    () => (subscribed ? topTenFlashes : topTenFlashes.slice(0, PREVIEW_TRADE_FLASH_ROWS)),
    [topTenFlashes, subscribed]
  );
  const lockedTopTen = useMemo(
    () => (subscribed ? [] : topTenFlashes.slice(PREVIEW_TRADE_FLASH_ROWS)),
    [topTenFlashes, subscribed]
  );

  const renderLockedFeedBlock = (items, renderRow) => {
    if (items.length === 0) return null;
    return (
      <div className="relative w-full min-w-0 max-w-full overflow-hidden">
        <div className="pointer-events-none divide-y divide-slate-800/20 blur-[3px] select-none dark:divide-slate-800/50">
          {items.map((item, idx) => renderRow(item, idx + PREVIEW_TRADE_FLASH_ROWS))}
        </div>
        <SubscribeUnblurOverlay
          title="Unlock trade flash"
          description="Subscribe for the full live trade flash stream and institutional flow alerts."
        />
      </div>
    );
  };

  // Scroll to bottom, optionally smoothly (live feed column only)
  const scrollToBottom = useCallback((smooth = false) => {
    const el = feedContainerRef.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Track manual scrolling to disable auto-scroll
  const handleFeedScroll = useCallback(() => {
    const el = feedContainerRef.current;
    if (!el) return;
    isAtBottomRef.current =
      el.scrollTop + el.clientHeight >= el.scrollHeight - 300;
  }, []);

  const onTradeFlashMessage = useCallback((e) => {
    try {
      const data = JSON.parse(e.data);
      if (data?.event === 'hello') {
        setRecency(data.recency === 'delayed' ? 'delayed' : 'live');
        if (typeof data.delay_minutes === 'number') {
          setDelayMinutes(data.delay_minutes);
        }
        return;
      }
      if (data?.event === 'option_trade' && data.trade) {
        const trade = data.trade;
        setFlashes((prev) => [...prev, trade]);
      }
    } catch (_) {
      /* ignore malformed frames */
    }
  }, []);

  // Reconnects with capped, jittered backoff, and immediately when the browser
  // comes back online or the tab becomes visible.
  useEffect(() => {
    let ws = null;
    let stopped = false;
    let retryCount = 0;
    let reconnectTimer = null;

    const connect = () => {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
      if (stopped) return;
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = null;
        ws.close();
      }
      const socket = new WebSocket(tradeFlashSocketUrl(TRADE_FLASH_SOCKET_URL));
      ws = socket;
      socket.onopen = () => {
        retryCount = 0;
        setIsConnected(true);
      };
      socket.onmessage = onTradeFlashMessage;
      socket.onclose = () => {
        if (stopped || ws !== socket) return;
        ws = null;
        setIsConnected(false);
        retryCount += 1;
        const base = Math.min(1000 * 2 ** Math.min(retryCount, 5), 30000);
        reconnectTimer = setTimeout(connect, base / 2 + Math.random() * (base / 2));
      };
    };

    const wake = () => {
      if (stopped) return;
      if (!ws || ws.readyState === WebSocket.CLOSED || ws.readyState === WebSocket.CLOSING) {
        retryCount = 0;
        connect();
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'visible') wake();
    };
    // The old socket is dead once the network drops, even if it still reports OPEN.
    const onOffline = () => {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = null;
        ws.close();
        ws = null;
      }
      setIsConnected(false);
    };
    const onOnline = () => {
      if (stopped) return;
      retryCount = 0;
      connect();
    };

    connect();
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      stopped = true;
      clearTimeout(reconnectTimer);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
      if (ws) {
        ws.onopen = ws.onmessage = ws.onclose = null;
        ws.close();
      }
      setIsConnected(false);
    };
  }, [onTradeFlashMessage]);

  useEffect(() => {
    if (!subscribed) return;
    if (isAtBottomRef.current) scrollToBottom();
  }, [filteredFlashes, scrollToBottom, subscribed]);

  const handleAtBottomClick = () => {
    isAtBottomRef.current = true;
    scrollToBottom(true);
  };

  const tradeSummary = (item) => (
    <>
      <span className="font-bold">+{item.size}</span>{' '}
      <span className={item.type === 'CALL' ? 'text-green-500' : 'text-red-500'}>{item.underlying}</span> 100 {item.expiry}{' '}
      <span className="font-bold text-blue-500">
        {item.strike} {item.type}
      </span>{' '}
      @ <span className="text-emerald-500">{item.price}</span>, for{' '}
      <span className="font-black text-blue-400">{item.notional}</span> total deltas.
    </>
  );

  /** Full-width wrapping lines for the narrow Top 10 column */
  const renderTopTenRow = (item, idx, rank) => (
    <div
      key={`top-${item.timestamp}-${item.underlying}-${item.strike}-${item.type}-${idx}`}
      className={`group flex gap-2 px-2 py-2.5 transition-colors duration-200 hover:bg-blue-500/5 sm:gap-3 sm:px-3 ${
        theme === 'dark' ? 'bg-slate-900/20' : 'bg-white'
      }`}
    >
      <div className="flex w-10 shrink-0 flex-col items-center gap-0.5 pt-0.5 sm:w-11">
        <span
          className={`flex h-6 w-6 items-center justify-center rounded-md text-[11px] font-bold sm:h-7 sm:w-7 sm:text-xs ${
            rank <= 3
              ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
              : theme === 'dark'
                ? 'bg-slate-800 text-slate-400'
                : 'bg-slate-200 text-slate-600'
          }`}
        >
          {rank}
        </span>
        <span className="max-w-[2.75rem] text-center text-[9px] font-mono leading-tight text-slate-500 sm:text-[10px]">
          {formatLocalTime(item.timestamp)}
        </span>
      </div>
      <p
        className={`min-w-0 flex-1 text-xs leading-relaxed break-words [overflow-wrap:anywhere] sm:text-sm ${
          theme === 'dark' ? 'text-slate-300' : 'text-slate-700'
        }`}
      >
        {tradeSummary(item)}
      </p>
    </div>
  );

  const renderTradeRow = (item, idx, rank) => (
    <div
      key={`${item.timestamp}-${item.underlying}-${item.strike}-${item.type}-${idx}`}
      className={`group flex gap-3 px-3 py-2.5 transition-all duration-200 max-sm:items-start sm:items-center sm:px-4 hover:bg-blue-500/5 ${
        theme === 'dark' ? 'bg-slate-900/20' : 'bg-white'
      }`}
    >
      {rank != null && (
        <span
          className={`shrink-0 flex h-5 w-5 items-center justify-center rounded-md text-xs font-bold ${
            rank <= 3
              ? 'bg-amber-500/20 text-amber-600 dark:text-amber-400'
              : theme === 'dark'
                ? 'bg-slate-800 text-slate-400'
                : 'bg-slate-200 text-slate-600'
          }`}
        >
          {rank}
        </span>
      )}
      <span className="shrink-0 text-[10px] font-mono text-slate-500 w-16">
        {formatLocalTime(item.timestamp)}
      </span>
      <div className="min-w-0 flex-1 overflow-x-auto scrollbar-hide sm:flex sm:items-center sm:gap-2">
        <p
          className={`text-xs leading-snug sm:text-sm sm:leading-normal max-sm:break-words max-sm:whitespace-normal sm:whitespace-nowrap ${theme === 'dark' ? 'text-slate-300' : 'text-slate-700'}`}
        >
          {tradeSummary(item)}
        </p>
      </div>
    </div>
  );

  return (
    <div className="flex h-full min-h-0 w-full min-w-0 flex-col overflow-hidden px-2 pb-2 pt-2 sm:px-4 md:px-5">
      <ConnectionStatusBanner
        isConnected={isConnected}
        className={`mx-auto w-full max-w-6xl rounded-lg md:max-w-3xl lg:max-w-6xl ${isConnected ? "" : "mb-2"}`}
      />
      <div className="mx-auto flex min-h-0 w-full min-w-0 max-w-6xl flex-1 flex-col gap-3 overflow-hidden max-md:gap-3 md:max-w-3xl lg:max-w-6xl lg:flex-row lg:items-stretch lg:justify-center lg:gap-8">
        {/* Trade Flash: stacked until lg; left column on wide screens */}
        <section
          className={`flex min-h-0 w-full min-w-0 max-w-full flex-1 flex-col overflow-hidden rounded-xl border shadow-sm max-md:self-stretch lg:min-h-0 lg:min-w-0 lg:flex-1 ${
            theme === 'dark'
              ? 'border-slate-800 bg-slate-900/40'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div
            className={`relative z-20 flex shrink-0 flex-col gap-3 overflow-visible border-b p-3 sm:p-4 lg:flex-row lg:items-start lg:justify-between lg:gap-4 ${
              theme === 'dark' ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'
            }`}
          >
            <div className="flex min-w-0 items-center gap-3 pr-1">
              <div className="shrink-0 rounded-lg bg-yellow-500/10 p-2 text-yellow-500">
                <HiOutlineLightningBolt size={24} className="animate-pulse" />
              </div>
              <div className="min-w-0 flex-1 flex flex-col gap-1">
                <h2 className={`text-base font-bold sm:text-lg ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>Trade Flash</h2>
                {subscribed ? (
                  <>
                    <RecencyBadge recency={recency} delayMinutes={delayMinutes} theme={theme} />
                    <p className="truncate text-[11px] leading-snug text-slate-500">
                      {selected
                        ? `Showing only ${selected} option trades from the ${delayed ? 'delayed' : 'live'} feed`
                        : delayed
                          ? `Big delta option trades, delayed ${delayMinutes} minutes`
                          : 'Real-time big delta option trades'}
                    </p>
                  </>
                ) : (
                  <MarketDataDelaySubscribeInline
                    theme={theme}
                    delayMinutes={delayMinutes}
                    stacked
                  />
                )}
              </div>
            </div>
            <div className="relative z-20 w-full min-w-0 shrink-0 overflow-visible lg:max-w-[17.5rem] lg:pt-0.5 xl:max-w-xs">
              <SelectTicker selected={selected} setSelected={setSelected} />
            </div>
          </div>

          <div className={`relative flex min-h-0 flex-1 flex-col overflow-hidden ${theme === 'dark' ? 'bg-[#0f172a]' : 'bg-slate-50'}`}>
            <div
              ref={feedContainerRef}
              onScroll={subscribed ? handleFeedScroll : undefined}
              className={`flex min-h-0 flex-1 flex-col overscroll-contain scrollbar-hide p-0 ${
                subscribed ? "overflow-y-auto" : "overflow-hidden"
              }`}
            >
              {filteredFlashes.length > 0 ? (
                <div className="divide-y divide-slate-800/20 dark:divide-slate-800/50">
                  {previewFlashes.map((item, idx) => renderTradeRow(item, idx))}
                  {!subscribed && renderLockedFeedBlock(lockedFlashes, renderTradeRow)}
                </div>
              ) : (
                <div className="flex flex-1 flex-col items-center justify-center px-4 py-12 text-center space-y-4">
                  <div
                    className={`flex h-16 w-16 items-center justify-center rounded-full border ${theme === 'dark' ? 'border-slate-800 bg-slate-900' : 'border-slate-300 bg-slate-200'}`}
                  >
                    <HiOutlineLightningBolt className="text-slate-700" size={32} />
                  </div>
                  <div className="space-y-1">
                    {selected && flashes.length > 0 ? (
                      <>
                        <p className="font-medium text-slate-400">No {selected} trades in the feed yet</p>
                        <p className="text-xs text-slate-600">Trades for other symbols are still being collected.</p>
                      </>
                    ) : (
                      <>
                        <p className="font-medium text-slate-400">Waiting for market activity...</p>
                        <p className="text-xs text-slate-600">
                          {delayed
                            ? `Trades appear here ${delayMinutes} minutes after they print.`
                            : 'Real-time trades will appear here as they happen.'}
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}
            </div>

            {subscribed && !isAtBottomRef.current && (
              <button
                type="button"
                onClick={handleAtBottomClick}
                className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2 rounded-full bg-blue-600 px-4 py-2 text-white shadow-2xl transition-all duration-300 animate-bounce hover:bg-blue-500"
              >
                <HiOutlineArrowCircleDown size={20} />
                <span className="text-xs font-bold uppercase tracking-wider">New Trades Below</span>
              </button>
            )}
          </div>
        </section>

        {/* Top 10: below feed until lg; fixed-width sidebar on wide screens */}
        <section
          className={`flex w-full min-h-0 min-w-0 max-w-full flex-col overflow-hidden rounded-xl border shadow-sm max-lg:max-h-[40dvh] max-lg:w-full max-lg:shrink-0 max-lg:self-center lg:h-full lg:max-h-none lg:w-[min(26rem,32%)] lg:max-w-[28rem] lg:flex-none lg:self-stretch ${
            theme === 'dark'
              ? 'border-slate-800 bg-slate-900/40'
              : 'border-slate-200 bg-white'
          }`}
        >
          <div className={`w-full shrink-0 border-b p-2.5 sm:p-4 ${theme === 'dark' ? 'border-slate-800 bg-slate-900/50' : 'border-slate-100 bg-slate-50/50'}`}>
            <div className="mx-auto flex max-w-xl flex-row items-center gap-2 text-left sm:gap-3">
              <div className="shrink-0 rounded-lg bg-yellow-500/10 p-1.5 text-yellow-500 sm:p-2">
                <HiOutlineLightningBolt size={22} className="animate-pulse sm:h-6 sm:w-6" />
              </div>
              <div className="min-w-0">
                <h2 className={`text-base font-bold sm:text-lg ${theme === 'dark' ? 'text-slate-200' : 'text-slate-900'}`}>Top 10 Trade Flash</h2>
                <p className="line-clamp-2 text-[11px] text-slate-500 sm:text-xs">Top 10 by total deltas</p>
              </div>
            </div>
          </div>

          <div
            className={`relative flex min-h-0 w-full flex-1 flex-col overflow-hidden md:flex md:flex-1 ${theme === 'dark' ? 'bg-[#0f172a]' : 'bg-slate-50'}`}
          >
            <div
              className={`min-h-0 w-full flex-1 overscroll-contain scrollbar-hide px-2 py-0 ${
                subscribed ? "overflow-y-auto" : "overflow-hidden"
              }`}
            >
              {topTenFlashes.length > 0 ? (
                <div className="w-full divide-y divide-slate-800/20 dark:divide-slate-800/50">
                  {previewTopTen.map((item, idx) => renderTopTenRow(item, idx, idx + 1))}
                  {!subscribed &&
                    lockedTopTen.length > 0 && (
                      <div className="relative w-full min-w-0 max-w-full overflow-hidden">
                        <div className="pointer-events-none blur-[3px] select-none">
                          {lockedTopTen.map((item, idx) =>
                            renderTopTenRow(item, idx, idx + 1 + PREVIEW_TRADE_FLASH_ROWS)
                          )}
                        </div>
                        <SubscribeUnblurOverlay
                          title="Unlock top trades"
                          description="Subscribe to see the complete top trade rankings and daily leaders."
                        />
                      </div>
                    )}
                </div>
              ) : (
                <div className="flex min-h-[12rem] w-full flex-col items-center justify-center px-4 py-8 text-center space-y-4">
                  <div className={`flex h-16 w-16 items-center justify-center rounded-full border ${theme === 'dark' ? 'border-slate-800 bg-slate-900' : 'border-slate-300 bg-slate-200'}`}>
                    <HiOutlineLightningBolt className="text-slate-700" size={32} />
                  </div>
                  <div className="space-y-1">
                    <p className="font-medium text-slate-400">Waiting for market activity...</p>
                    <p className="text-xs text-slate-600">Top quantity trades will rank here as the feed fills.</p>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
};

export default TradeFlash;
